import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import {
  createDifficulties,
  getDifficultyByID,
  editDifficulty,
} from '../../store/actions/difficulties/difficulties.action';
import CreateDifficulties from '../../components/Difficulties/CreateDifficulties';

// Loading component extracted for better organization
const LoadingSpinner = () => (
  <div className="loader">
    <svg viewBox="0 0 32 32" width="32" height="32">
      <circle id="spinner" cx="16" cy="16" r="14" fill="none" />
    </svg>
  </div>
);

// Access denied component
const AccessDenied = () => (
  <div className="access-denied">
    This category is not accessible to the logged in user.
  </div>
);

const CreateDifficultiesContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Redux state
  const { isCreating, difficultyToBeEdit, categories } = useSelector(state => ({
    isCreating: state.difficulties.isCreating,
    difficultyToBeEdit: state.difficulties.difficultyToBeEdit,
    categories: state.auth.categories,
  }));

  // Local state
  const [mode, setMode] = useState('CREATE');
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [difficulty, setDifficulty] = useState(null);

  // Set access permissions based on categories
  const setAccess = useCallback(() => {
    try {
      if (!categories || categories.length === 0) return;

      const difficultyCategory = categories.find(category => category.link === 'difficulties');

      if (difficultyCategory) {
        setAccessMode(difficultyCategory.mode);
        setAccessDenied(false);
      } else {
        setAccessDenied(true);
      }
    } catch (err) {
      console.error('Error setting access:', err);
      setAccessDenied(true);
    }
  }, [categories]);

  // Fetch difficulty data for edit mode
  const fetchDifficulty = useCallback(async (difficultyID) => {
    try {
      setLoading(true);
      const result = await dispatch(getDifficultyByID(difficultyID));
      setDifficulty(result);
    } catch (error) {
      console.error('Error fetching difficulty:', error);
      navigate('/difficulties');
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate]);

  // Handle form submission for create
  const handleCreate = useCallback(async (values) => {
    try {
      await dispatch(createDifficulties(values));
      navigate('/difficulties');
    } catch (error) {
      console.error('Error creating difficulty:', error);
    }
  }, [dispatch, navigate]);

  // Handle form submission for edit
  const handleEdit = useCallback(async (values) => {
    try {
      await dispatch(editDifficulty(id, values));
      navigate('/difficulties');
    } catch (error) {
      console.error('Error editing difficulty:', error);
    }
  }, [dispatch, navigate, id]);

  // Set mode and fetch data on mount and when id changes
  useEffect(() => {
    const newMode = id ? 'EDIT' : 'CREATE';
    setMode(newMode);

    if (id) {
      fetchDifficulty(id);
    }
  }, [id, fetchDifficulty]);

  // Set access when categories change
  useEffect(() => {
    setAccess();
  }, [setAccess]);

  // Show loading spinner for edit mode while fetching data
  if (mode === 'EDIT' && (loading || (!difficulty && !difficultyToBeEdit))) {
    return <LoadingSpinner />;
  }

  // Show access denied message
  if (accessDenied) {
    return <AccessDenied />;
  }

  // Get initial values for the form
  const getInitialValues = () => {
    return difficulty || difficultyToBeEdit || null;
  };

  return (
    <section>
      <CreateDifficulties
        accessMode={accessMode}
        onCreate={handleCreate}
        onEdit={handleEdit}
        initialValues={getInitialValues()}
        mode={mode}
        isCreating={isCreating}
      />
    </section>
  );
};

export default CreateDifficultiesContainer;
